import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseDocumentList } from '../utils/helpers';

const Careers = () => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPos, setSelectedPos] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const positionsPerPage = 8;

  useEffect(() => {
    fetch('/api/careers/positions')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setPositions(data);
        else setError(data.message || 'Failed to load positions');
      })
      .catch(() => setError('Failed to load positions'))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.max(1, Math.ceil(positions.length / positionsPerPage));
  const currentPositions = positions.slice((currentPage - 1) * positionsPerPage, currentPage * positionsPerPage);
  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderRequirements = (req) => {
    if (!req) return null;
    return req.split('\n').filter(l => l.trim()).map((line, i) => (
      <li key={i} className="flex items-start gap-2">
        <svg className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span>{line.trim().replace(/^[-•]\s*/, '')}</span>
      </li>
    ));
  };

  const getExperienceLine = (req) => {
    if (!req) return null;
    const lines = req.split('\n').map(l => l.trim()).filter(Boolean);
    return lines.find(l => /experience/i.test(l)) || lines[0] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-800 text-white py-5 sm:py-7 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 0, transparent 30%), radial-gradient(circle at 80% 70%, white 0, transparent 25%)' }}></div>
        <div className="relative max-w-6xl mx-auto flex items-center justify-center text-center gap-3 sm:gap-4">
          <img src="/school.png" alt="Hope Haven Christian School" className="h-10 sm:h-12 md:h-14 rounded-lg bg-white p-1 shadow-lg" />
          <div>
            <h1 className="text-lg sm:text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">Hope Haven Christian School</h1>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className="h-px w-6 sm:w-10 bg-blue-300/60"></span>
              <p className="text-xs sm:text-sm md:text-base text-blue-100 font-semibold uppercase tracking-widest">Careers & Opportunities</p>
              <span className="h-px w-6 sm:w-10 bg-blue-300/60"></span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-8 sm:py-12">
        {/* Intro */}
        <section className="mb-10 sm:mb-14 text-center max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">Work With Us</h2>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Hope Haven Christian School is committed to transforming students and families through Christ-centered
            education and uncompromising excellence. We are always looking for talented, passionate professionals
            to join our team. Explore our current openings below and submit your application.
          </p>
        </section>

        {/* Important Notice */}
        <section className="mb-10 sm:mb-14 max-w-3xl mx-auto bg-amber-50 border-2 border-amber-400/60 rounded-xl p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <svg className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.224-.833-2.994 0L3.34 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="font-bold text-amber-900 mb-1">Important Notice</h3>
              <p className="text-amber-800 text-sm sm:text-base leading-relaxed">
                Hope Haven Christian School does <strong>not charge any fee</strong> at any stage of the recruitment process.
                We do not ask applicants for money in exchange for job applications, interviews, employment, or any other
                recruitment-related service.
              </p>
              <p className="text-amber-800 text-sm sm:text-base leading-relaxed mt-2">
                Please be cautious of anyone requesting payment while claiming to represent Hope Haven Christian School.
                Only apply through our official recruitment channels.
              </p>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-8 max-w-3xl mx-auto bg-red-50 border-l-4 border-red-600 p-4 rounded">
            <p className="text-red-800 font-semibold text-sm sm:text-base">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading positions...</p>
          </div>
        ) : positions.length === 0 ? (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
              <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">No Open Positions</h3>
            <p className="text-gray-600 text-sm sm:text-base">
              There are no open positions at the moment. Please check back later or send your CV to{' '}
              <a href="mailto:careers@hopehavenschool.org" className="text-blue-600 font-semibold">careers@hopehavenschool.org</a>.
            </p>
          </div>
        ) : (
          <>
            {/* Grid heading */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Current Openings</h3>
              <span className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                {positions.length} open position{positions.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-4 sm:gap-5">
              {currentPositions.map((pos) => (
                <div key={pos.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full overflow-hidden">
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    {/* Badges */}
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 truncate max-w-[65%]">
                        {pos.department || 'General'}
                      </span>
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide truncate ml-2">{pos.employmentType}</span>
                    </div>

                    <h3 className="text-base font-bold text-gray-900 leading-snug [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">{pos.title}</h3>

                    {/* Meta */}
                    <div className="mt-2.5 space-y-1 text-sm text-gray-500">
                      <p className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="truncate">{pos.location || 'Kigali, Rwanda'}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="truncate">{pos.salaryRange || 'Negotiable'}</span>
                      </p>
                    </div>

                    {pos.description && (
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">
                        {pos.description}
                      </p>
                    )}

                    {getExperienceLine(pos.requirements) && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-gray-600">
                        <svg className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="truncate leading-relaxed">{getExperienceLine(pos.requirements).replace(/^[-•]\s*/, '')}</span>
                      </div>
                    )}

                    {pos.deadline && (
                      <div className="mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-2.5 py-1.5">
                        <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">Apply by {new Date(pos.deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}

                    {parseDocumentList(pos.requiredDocuments).length > 0 && (
                      <p className="mt-2 text-xs text-blue-700 font-medium bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5">
                        {parseDocumentList(pos.requiredDocuments).length} required document{parseDocumentList(pos.requiredDocuments).length !== 1 ? 's' : ''}
                      </p>
                    )}

                    <div className="mt-auto pt-4">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSelectedPos(pos)}
                          className="rounded-lg border-2 border-blue-600 text-blue-700 hover:bg-blue-50 font-bold py-2 px-3 text-xs transition duration-200"
                        >
                          Full Details
                        </button>
                        <button
                          onClick={() => navigate('/careers/apply', { state: { positionId: pos.id, positionTitle: pos.title } })}
                          className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 px-3 rounded-lg text-xs shadow transition duration-200"
                        >
                          Apply Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-sm transition duration-200"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-10 h-10 rounded-lg font-semibold text-sm transition duration-200 ${
                      page === currentPage
                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-sm transition duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Full Details Modal */}
        {selectedPos && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/60 backdrop-blur-sm overflow-y-auto"
            onClick={() => setSelectedPos(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-gray-100">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                      {selectedPos.department || 'General'}
                    </span>
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{selectedPos.employmentType}</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">{selectedPos.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-1.5">
                      <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {selectedPos.location || 'Kigali, Rwanda'}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {selectedPos.salaryRange || 'Negotiable'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPos(null)}
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition"
                  aria-label="Close"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal body */}
              <div className="px-6 sm:px-8 py-5 overflow-y-auto">
                {selectedPos.deadline && (
                  <div className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Apply by {new Date(selectedPos.deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                )}
                {selectedPos.jobAdvertPath && (
                  <div className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <a href={`/uploads/${selectedPos.jobAdvertPath}`} target="_blank" rel="noopener noreferrer" className="hover:underline">Download Job Advert (PDF)</a>
                  </div>
                )}
                {selectedPos.description && (
                  <div>
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Job Description</h4>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{selectedPos.description}</p>
                  </div>
                )}
                {selectedPos.requirements && (
                  <div className="mt-6">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Requirements</h4>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      {renderRequirements(selectedPos.requirements)}
                    </ul>
                  </div>
                )}
                {parseDocumentList(selectedPos.requiredDocuments).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Required Documents</h4>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      {parseDocumentList(selectedPos.requiredDocuments).map(doc => (
                        <li key={doc} className="flex items-start gap-2">
                          <svg className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="px-6 sm:px-8 py-5 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    setSelectedPos(null);
                    navigate('/careers/apply', { state: { positionId: selectedPos.id, positionTitle: selectedPos.title } });
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2.5 px-5 rounded-lg text-sm shadow transition duration-200"
                >
                  Apply Now
                </button>
                <button
                  onClick={() => setSelectedPos(null)}
                  className="sm:flex-none px-6 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-sm transition duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <section className="mt-12 sm:mt-16 max-w-3xl mx-auto bg-blue-50 border-l-4 border-blue-600 p-5 sm:p-7 rounded-xl shadow-sm">
          <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">Don't see a suitable position?</h3>
          <p className="text-gray-700 text-sm sm:text-base">
            You can still send your CV and cover letter to{' '}
            <a href="mailto:careers@hopehavenschool.org" className="text-blue-600 font-semibold">careers@hopehavenschool.org</a>.
            We keep applications on file and contact candidates when a relevant position opens.
          </p>
          <p className="text-gray-700 text-sm sm:text-base mt-3">
            Looking for student admissions? Visit{' '}
            <a href="https://hopehavenschool.ddns.net" className="text-blue-600 font-semibold">hopehavenschool.ddns.net</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Careers;
